/**
 * Course names as a learner reads them. Moodle's English titles are in capitals, and the server's default short name is cut from
 * them, so on real data a chip read "INFINITESI…". The server stays as it is; the web shows the canvas's name for a known course
 * code, and otherwise the title with its capitals fixed. A short name the learner chose is always shown as chosen.
 */

import type { UiLocale } from '../i18n/locale.js';

interface NameableCourse {
  readonly code: string;
  readonly title: { readonly en: string; readonly he: string };
  readonly shortName: string;
  readonly shortNameIsDefault: boolean;
}

const KNOWN: Readonly<Record<string, Readonly<Record<UiLocale, string>>>> = {
  '80131': { en: 'Calculus 1', he: 'חדו״א 1' },
  '80134': { en: 'Linear Algebra 1', he: 'לינארית 1' },
  '80187': { en: 'Discrete Math', he: 'בדידה' },
};

const SMALL_WORDS = new Set(['of', 'and', 'the', 'in', 'to', 'for', 'on', 'a', 'an']);

/** "INFINITESIMAL CALCULUS (1)" becomes "Infinitesimal Calculus (1)". Only an all-capitals Latin title is touched. */
function fixCapitals(title: string): string {
  if (!/[A-Za-z]/.test(title) || /[a-z]/.test(title)) return title;
  return title
    .split(' ')
    .map((word, index) => {
      if (/^[IVX]+$/.test(word)) return word;
      const lower = word.toLowerCase();
      if (index > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.replace(/[a-z]/, (letter) => letter.toUpperCase());
    })
    .join(' ');
}

export function courseTitle(course: Pick<NameableCourse, 'title'>, locale: UiLocale): string {
  return locale === 'he' ? course.title.he : fixCapitals(course.title.en);
}

export function courseShortName(course: NameableCourse, locale: UiLocale): string {
  if (!course.shortNameIsDefault) return course.shortName;
  return KNOWN[course.code]?.[locale] ?? courseTitle(course, locale);
}
